import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AppAutocomplete, AppButton, AppFormField, AppInput, AppModal } from '../../components/ui';
import clientAnnuaireService from '../../services/clientAnnuaireService';
import clientPropositionService from '../../services/clientPropositionService';
import { notify } from '../../components/common/AppToast';
import { hubspotCompanyHref, identifiantHref, identifiantsOf } from '../../utils/clientIdentifiants';

const ClientIdentifiantsDrawer = ({ show, row, onClose, onSaved }) => {
  const ids = identifiantsOf(row);
  const [draft, setDraft] = useState(() => draftFrom(ids));
  const [saving, setSaving] = useState(false);
  const [activeSource, setActiveSource] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [ragEditing, setRagEditing] = useState(false);
  const [history, setHistory] = useState([]);
  const requestRef = useRef(0);
  const committedRef = useRef({ hubspot: '', drive: '' });

  useEffect(() => {
    const next = identifiantsOf(row);
    setDraft(draftFrom(next));
    setActiveSource(null);
    setSearchResults([]);
    setRagEditing(false);
    committedRef.current = {
      hubspot: next.hubspot_id || '',
      drive: next.drive_folder_id || '',
    };
  }, [row]);

  useEffect(() => {
    if (!show || !row?.id) return undefined;
    let cancelled = false;
    clientAnnuaireService.get(row.id).then((client) => {
      if (!cancelled) setHistory(Array.isArray(client?.propositions) ? client.propositions : []);
    }).catch(() => {
      if (!cancelled) setHistory([]);
    });
    return () => { cancelled = true; };
  }, [show, row?.id]);

  const ragShown = ragEditing ? draft.slug_rag : (draft.slug_rag || draft.slug);
  const searchQuery = activeSource === 'hubspot'
    ? draft.hubspot_id
    : activeSource === 'drive'
      ? draft.drive_folder_id
      : activeSource === 'rag'
        ? ragShown
        : '';

  useEffect(() => {
    if (!show || !activeSource) {
      return undefined;
    }
    const query = String(searchQuery || '').trim();
    const committed = activeSource === 'hubspot'
      ? committedRef.current.hubspot
      : activeSource === 'drive'
        ? committedRef.current.drive
        : '';
    if (query.length < 2 || (committed !== '' && query === committed)) {
      setSearchResults([]);
      setSearching(false);
      return undefined;
    }
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const items = await clientPropositionService.searchSource(activeSource, query);
        if (requestRef.current === requestId) {
          setSearchResults(items);
        }
      } catch (err) {
        if (requestRef.current === requestId) {
          notify.error(err.message || 'Recherche impossible');
          setSearchResults([]);
        }
      } finally {
        if (requestRef.current === requestId) {
          setSearching(false);
        }
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [show, activeSource, searchQuery]);

  const dolibarrHref = identifiantHref(ids, 'dolibarr');
  const hubspotHref = identifiantHref({ ...ids, hubspot_id: draft.hubspot_id }, 'hubspot') || ids.hubspot_url;
  const driveHref = draft.drive_folder_id && draft.drive_folder_id === committedRef.current.drive
    ? `https://drive.google.com/drive/folders/${draft.drive_folder_id}`
    : '';

  const chooseHubspot = (item) => {
    committedRef.current.hubspot = item.identifiant;
    setDraft((prev) => ({ ...prev, hubspot_id: item.identifiant }));
    setActiveSource(null);
  };

  const chooseDrive = (item) => {
    const nextSlug = item.extra || '';
    committedRef.current.drive = item.identifiant;
    setDraft((prev) => {
      const followsDrive = prev.slug_rag === '' || prev.slug_rag === prev.slug;
      return {
        ...prev,
        drive_folder_id: item.identifiant,
        slug: nextSlug,
        slug_rag: followsDrive ? '' : prev.slug_rag,
      };
    });
    setRagEditing(false);
    setActiveSource(null);
  };

  const chooseRag = (item) => {
    setDraft((prev) => ({ ...prev, slug_rag: item.identifiant }));
    setRagEditing(false);
    setActiveSource(null);
  };

  const save = async () => {
    if (!row?.id) return;
    setSaving(true);
    try {
      const updated = await clientAnnuaireService.updateIdentifiants(row.id, {
        hubspot_id: draft.hubspot_id || null,
        drive_folder_id: draft.drive_folder_id || null,
        slug: draft.slug || null,
        slug_rag: draft.slug_rag && draft.slug_rag !== draft.slug ? draft.slug_rag : null,
      });
      notify.success('Identifiants enregistrés');
      onSaved?.(updated);
      onClose?.();
    } catch (err) {
      notify.error(err.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const pending = useMemo(
    () => history.filter((item) => item.statut === 'en_attente'),
    [history],
  );

  if (!row) return null;

  const suggestionsFor = (source) => (activeSource === source ? searchResults : []);

  return (
    <AppModal
      show={show}
      title={row.nom || 'Fiche identifiants'}
      onClose={onClose}
      size="lg"
      scrollable
      footer={(
        <>
          <AppButton variant="outline-secondary" size="sm" onClick={onClose}>Fermer</AppButton>
          <AppButton size="sm" loading={saving} onClick={save}>Enregistrer</AppButton>
        </>
      )}
    >
      <p className="text-secondary-light mb-3">
        Dolibarr #{ids.dolibarr_id}
        {ids.dolibarr_code ? ` · ${ids.dolibarr_code}` : ''}
        {dolibarrHref ? (
          <>
            {' · '}
            <a href={dolibarrHref} target="_blank" rel="noopener noreferrer">ouvrir la fiche</a>
          </>
        ) : null}
      </p>

      <AppFormField label="HubSpot" className="mb-3">
        <div className="d-flex gap-2 flex-wrap align-items-start">
          <AppAutocomplete
            className="client-annuaire-source-search"
            size="sm"
            value={draft.hubspot_id}
            placeholder="Rechercher une entreprise HubSpot"
            items={suggestionsFor('hubspot')}
            loading={activeSource === 'hubspot' && searching}
            minQueryLength={draft.hubspot_id === committedRef.current.hubspot ? 9999 : 2}
            getItemKey={(item) => item.identifiant}
            renderItem={(item) => (
              <>
                <strong>{item.libelle}</strong>
                <span className="text-secondary-light ms-2">{item.identifiant}</span>
              </>
            )}
            onFocus={() => setActiveSource('hubspot')}
            onChange={(value) => setDraft((prev) => ({ ...prev, hubspot_id: value }))}
            onSelect={chooseHubspot}
          />
          {hubspotHref ? (
            <AppButton as="a" variant="link" size="sm" href={hubspotHref} target="_blank" rel="noopener noreferrer">
              Ouvrir
            </AppButton>
          ) : null}
        </div>
      </AppFormField>

      <AppFormField label="Drive" className="mb-3">
        <div className="d-flex gap-2 flex-wrap align-items-start">
          <AppAutocomplete
            className="client-annuaire-source-search"
            size="sm"
            value={draft.drive_folder_id}
            placeholder="Rechercher un dossier Drive"
            items={suggestionsFor('drive')}
            loading={activeSource === 'drive' && searching}
            minQueryLength={draft.drive_folder_id === committedRef.current.drive ? 9999 : 2}
            getItemKey={(item) => item.identifiant}
            renderItem={(item) => (
              <>
                <strong>{item.libelle}</strong>
                {item.extra ? <span className="text-secondary-light ms-2">{item.extra}</span> : null}
              </>
            )}
            onFocus={() => setActiveSource('drive')}
            onChange={(value) => setDraft((prev) => ({ ...prev, drive_folder_id: value }))}
            onSelect={chooseDrive}
          />
          <AppInput
            size="sm"
            value={draft.slug}
            onChange={(event) => {
              const slug = event.target.value;
              setDraft((prev) => {
                const followsDrive = prev.slug_rag === '' || prev.slug_rag === prev.slug;
                return {
                  ...prev,
                  slug,
                  slug_rag: followsDrive ? '' : prev.slug_rag,
                };
              });
            }}
            placeholder="Slug"
          />
          {driveHref ? (
            <AppButton as="a" variant="link" size="sm" href={driveHref} target="_blank" rel="noopener noreferrer">
              Ouvrir
            </AppButton>
          ) : null}
        </div>
      </AppFormField>

      <AppFormField label="RAG" className="mb-3" hint="Reprend le slug Drive. Vous pouvez le remplacer.">
        <AppAutocomplete
          size="sm"
          value={ragShown}
          placeholder="Slug RAG"
          items={suggestionsFor('rag')}
          loading={activeSource === 'rag' && searching}
          getItemKey={(item) => item.identifiant}
          renderItem={(item) => (
            <>
              <strong>{item.libelle}</strong>
              {item.extra ? <span className="text-secondary-light ms-2">{item.extra}</span> : null}
            </>
          )}
          onFocus={() => {
            setRagEditing(true);
            setActiveSource('rag');
            setDraft((prev) => (prev.slug_rag === '' ? { ...prev, slug_rag: prev.slug } : prev));
          }}
          onBlur={() => {
            setRagEditing(false);
            setDraft((prev) => (
              prev.slug_rag === '' || prev.slug_rag === prev.slug
                ? { ...prev, slug_rag: '' }
                : prev
            ));
          }}
          onChange={(value) => setDraft((prev) => ({ ...prev, slug_rag: value }))}
          onSelect={chooseRag}
        />
      </AppFormField>

      {pending.length > 0 ? (
        <p className="mb-2">
          {pending.length} proposition{pending.length > 1 ? 's' : ''} en attente — à valider dans Correspondances clients.
        </p>
      ) : null}

      {history.length > 0 ? (
        <div>
          <h3 className="h6">Historique des propositions</h3>
          <ul className="list-unstyled mb-0">
            {history.slice(0, 8).map((item) => (
              <li key={item.id} className="text-secondary-light small">
                {item.source} · {item.libelle_propose} · {item.statut}
                {item.score != null ? ` · ${Math.round(item.score * 100)} %` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </AppModal>
  );
};

function draftFrom(ids) {
  return {
    hubspot_id: ids.hubspot_id || '',
    drive_folder_id: ids.drive_folder_id || '',
    slug: ids.slug || '',
    slug_rag: ids.slug_rag && ids.slug_rag !== ids.slug ? ids.slug_rag : '',
  };
}

export default ClientIdentifiantsDrawer;
